import { RouteObject, defer } from 'react-router-dom'

import { isAppStateReady } from '$appStore'
import {
  NonReactRoute,
  PageLoadingSupport,
  FullPageError,
  TopbarPortal,
} from '$components/common'
import { AppDialogProvider, SideNavAwareFileViewerProvider } from '$hooks'
import { appConfig, checkAuthentictionOrRedirectToLogin } from '$utils'

import AppRoot from './AppRoot'
import { venuesRoutes } from './routes/settings/billing-venues/Routes'
import { newVenueRoutes } from './routes/settings/new-venue/Routes'
import { companyRoutes } from './routes/settings/company/Routes'
import { profileRoutes } from './routes/settings/profile/Routes'
import { billingRoutes } from './routes/settings/billing/Routes'
import { subscriptionRoutes } from './routes/settings/subscription/Routes'
import { usersRoutes } from './routes/settings/users/Routes'
import { venueOnboardingRoutes } from './routes/settings/venue-onboarding/Routes'
import { complianceRoutes } from './routes/time/compliance/Routes'
import { timeclockRoutes } from './routes/time/timeclock/Routes'
import { payrollRoutes } from './routes/time/payroll/Routes'
import { workforceRoutes } from './routes/time/workforce/Routes'
import { rosterRoutes } from './routes/time/roster/Routes'
import { invoicesRoutes } from './routes/stock/invoices/Routes'
import { connectionRoutes } from './routes/connection/Routes'
import { stockReportsRoutes } from './routes/stock/reports/Routes'
import { menusRoutes } from './routes/stock/menus/Routes'
import { stockOrderingRoutes } from './routes/stock/ordering/Routes'
import { stockSupplierRoutes } from './routes/stock/suppliers/Routes'
import { publicRoutes } from './routes/public/Routes'

const isAngularJSHostPage = !appConfig.get<boolean>('standalone')

const publicLoader = async () => {
  return defer({
    data: await isAppStateReady(),
  })
}

const authLoader = async () => {
  checkAuthentictionOrRedirectToLogin()
  return defer({
    data: await isAppStateReady(),
  })
}

/**
 * Common definition of possible values used in the `handle` property of a route.
 *
 * The handle property allows any values to be passed to routes, and is accessible
 * via the `useMatches` hook there. We define the expected values here so it's
 * documented.
 */
export type RouteHandle =
  | {
      noTopbar?: boolean
    }
  | undefined

/**
 * A `react-router` router definition array. Each object in this array
 * represents a url that should have a React component rendered on it.
 *
 * Note that while it might seem tidy to push the `PageLoadingSupport`
 * component into each of the page definitions themselves it's not done
 * that way so that we don't end up with the code for that included in
 * each page bundle. Our app bundler is splitting the pages into separate
 * files that get downloaded, this way the `PageLoadingSupport` component
 * is only included once in the main bundle.
 */
export default [
  {
    element: (
      <>
        {/*
          The topbar is rendered here, inside the root route element, so it sits
          within the RouterProvider context and can use router hooks
          (useNavigate via useReactNavigate/useAngularJSNavigate, used by the
          nav links and profile flyout). It still portals itself into the
          separate #loaded-topbar mount node (createPortal preserves React
          context, so router context flows through the portal). It is a sibling
          of the content stack - not inside AppRoot's Outlet - so it renders
          once and is not re-keyed by AppRoot's remountCounter.
        */}
        {/*
          AppDialogProvider is hoisted to wrap BOTH the topbar and the content
          so the topbar (profile flyout) can use useAppDialog (e.g. the venue
          info dialog). It only provides context + renders a body-portalled
          MUI dialog, so its position in the tree doesn't affect layout.
        */}
        <AppDialogProvider>
          <TopbarPortal />
          <PageLoadingSupport
            enableAngularJSCompatibility={isAngularJSHostPage}
          >
            <SideNavAwareFileViewerProvider>
              <AppRoot />
            </SideNavAwareFileViewerProvider>
          </PageLoadingSupport>
        </AppDialogProvider>
      </>
    ),
    loader: async () => {
      // We wait for the AppContext to be ready (i.e. it's got data available)
      // before we start rendering the app. This makes the interface for client
      // code to interact with the AppContext simpler as they can rely on it
      // being available when they need it.
      return defer({
        data: await isAppStateReady(),
      })
    },
    errorElement: <FullPageError marginLeft="230px" />,
    children: [
      // Public routes (no auth required)
      {
        loader: publicLoader,
        children: [...publicRoutes],
      },

      // Protected routes with auth loader
      {
        loader: authLoader, // Auth check for all protected routes
        children: [
          ...profileRoutes,
          ...billingRoutes,
          ...venuesRoutes,
          ...newVenueRoutes,
          ...companyRoutes,
          ...subscriptionRoutes,
          ...usersRoutes,
          ...venueOnboardingRoutes,
          ...complianceRoutes,
          ...timeclockRoutes,
          ...payrollRoutes,
          ...workforceRoutes,
          ...rosterRoutes,
          ...invoicesRoutes,
          ...stockReportsRoutes,
          ...stockOrderingRoutes,
          ...stockSupplierRoutes,
          ...menusRoutes,
          ...connectionRoutes,
        ],
      },

      {
        path: '*',
        element: <NonReactRoute />,
        loader: authLoader,
      },
    ],
  },
] as RouteObject[]
